import { withTranslation } from 'react-i18next';
import { constant, util } from '@helper';
import _ from 'lodash';
import { CompanyAPI, JobApplicationAPI } from '@services/api';
import React from 'react';
import {
  Affix,
  Avatar,
  Badge,
  Button,
  Col,
  Divider,
  Grid,
  IconButton,
  Panel,
  Placeholder,
  Row,
  Stack,
  Tooltip,
  Whisper,
  toaster,
} from 'rsuite';
import {
  FaHome,
  FaBuilding,
  FaSuitcase,
  FaFileAlt,
  FaUser,
  FaChevronLeft,
  FaStar,
  FaLink,
  FaClock,
  FaMapMarkerAlt,
  FaUsers,
  FaMagic,
} from 'react-icons/fa';
import PlaceholderGrid from 'rsuite/esm/Placeholder/PlaceholderGrid';
import PlaceholderParagraph from 'rsuite/esm/Placeholder/PlaceholderParagraph';
import utils from '@helper/utils';

const { withRouter } = util;

// The classes below are the exact Tailwind translation of the former
// `styles.scss`. Every descendant selector is preserved through arbitrary
// variants (`[&_...]`) so the cascade and specificity stay identical to the
// original stylesheet. The remaining class names (e.g. `company-info`) are
// only kept as selector hooks for those variants.

const MAIN_CONTENT_CLASS = [
  '[&_.rs-col-xs-24]:mt-8',
  '[&_.rs-row:first-child_.rs-col-xs-24]:mt-0',
  '[&_a]:text-[#595959]',
  '[&_a_span]:ml-[10px]',
  '[&_a:hover]:no-underline',
  '[&_a:hover_span]:text-[#212121]',
  '[&_a:hover_svg]:text-[#212121]',
].join(' ');

// The original nth-child/media spacing is expressed directly per item so
// Tailwind emits the breakpoints in ascending order (base -> 784 -> 1024).
const cultureColClass = (index: number): string => {
  const base = 'text-center !mt-6';

  if (index <= 1) return `${base} min-[784px]:!mt-0`;
  if (index === 2) return `${base} min-[784px]:!mt-9 min-[1024px]:!mt-0`;

  return `${base} min-[784px]:!mt-9`;
};

const WRAPPER_CARD_CLASS = [
  'rounded-t-[20px] rounded-b-none',
  '[&_span]:text-[1em]',
  "[&_img[alt='card-header']]:h-[326px]",
  "[&_img[alt='card-header']]:object-cover",
].join(' ');

const DETAILS_CARD_CLASS = [
  'bg-white text-[#595959] border border-[#E4E4E4]',
  'rounded-t-none rounded-b-[20px]',
  'px-[10px] py-5 transition-all duration-[400ms]',
  // Remove horizontal padding on mobile so the card content uses the
  // full viewport width.
  'max-[425px]:!px-0',
  "[&_img[alt='company-culture-logo']]:block",
  // eslint-disable-next-line max-len
  "[&_img[alt='company-culture-logo']]:h-16 [&_img[alt='company-culture-logo']]:w-16",
  "[&_img[alt='company-culture-logo']]:object-cover",
  "[&_img[alt='company-culture-logo']]:mx-auto",
  "[&_img[alt='company-culture-logo']]:mb-2",
  "[&_img[alt='company-culture-logo']+b]:text-[1.2em]",
  '[&_p.culture-text]:!mt-2 [&_p.culture-text]:!text-[1em]',
  '[&_.loading-skeleton>.rs-stack-item]:w-full',
  '[&_svg]:text-[#004AAD]',
  '[&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:mt-2',
  '[&_p.company-details-title]:text-[#212121]',
  '[&_p.company-details-title]:font-bold',
  '[&_p.company-details-title]:text-[1.1em]',
  '[&_p.company-details-title]:leading-[25.2px]',
  '[&_p.company-details-title]:mb-2',
  '[&_h3.company-details-title]:text-[2em]',
  '[&_h3.company-details-title]:mr-2',
].join(' ');

const SECTION_CARD_EXTRA_CLASS =
  '!mt-6 !rounded-[20px] [&_.rs-panel]:!bg-white';

const SIDE_CARD_CLASS = [
  'w-full max-[425px]:mt-6 bg-white text-[#595959]',
  'border border-[#E4E4E4] rounded-[20px]',
  'px-[10px] pt-5 pb-0 transition-all duration-[400ms]',
  '[&_.rs-row>.rs-col]:mb-4',
  '[&_.rs-row>.rs-col:last-child]:mb-0',
  '[&_.rs-stack-item]:w-full',
  '[&_p]:text-[1em] [&_a]:text-[1em]',
  '[&_.rs-col-xs-3_svg]:text-[#004AAD]',
  '[&_.rs-col-xs-3_svg]:min-h-[48px]',
  '[&_p.company-details-attribute-title]:text-[#212121]',
  '[&_p.company-details-attribute-title]:font-bold',
  '[&_p.company-details-attribute-title]:text-[1em]',
  '[&_p.company-details-attribute-title]:leading-[25.2px]',
  '[&_p.company-details-attribute-title+a]:break-words',
].join(' ');

const SEARCH_RESULT_CARD_CLASS = [
  'bg-white text-[#E4E4E4] cursor-pointer',
  'min-h-[400px] mt-6 transition-all duration-[400ms]',
  '!rounded-[20px] border border-[#E4E4E4]',
  // Keep the mobile height as a floor only: a fixed height makes the
  // flex stack items shrink and collide/clip when content overflows.
  'max-[425px]:!min-h-[372px]',
  'shadow-[0_5px_10px_rgb(0_0_0_/_10%)]',
  'hover:shadow-[0_10px_20px_rgb(0_0_0_/_15%)]',
  "[&_img[alt='card-header']]:w-full",
  "[&_img[alt='card-header']]:aspect-[2/1]",
  "max-[425px]:[&_img[alt='card-header']]:aspect-[1.33/1]",
  "[&_img[alt='card-header']]:max-h-[187.5px]",
  "max-[1199px]:[&_img[alt='card-header']]:max-h-[180px]",
  "max-[425px]:[&_img[alt='card-header']]:max-h-[210px]",
  "[&_img[alt='card-header']]:object-cover",
  "[&_img[alt='company-logo']]:h-12 [&_img[alt='company-logo']]:w-12",
  "[&_img[alt='company-logo']]:object-contain",
  "[&_img[alt='company-logo']]:rounded-full",
  "[&_img[alt='company-logo']]:absolute",
  "[&_img[alt='company-logo']]:top-1/2",
  "[&_img[alt='company-logo']]:left-[7.5%]",
  "[&_img[alt='company-logo']]:bg-white",
  "[&_img[alt='logo-startup']]:!object-contain",
  "[&_img[alt='logo-startup']]:bg-white",
  '[&_svg]:!mt-0 [&_svg]:text-[#004AAD]',
  '[&_p]:text-[#595959] [&_p]:leading-[19.2px] [&_p]:text-[100%]',
  '[&_p]:break-words',
  // Never let stacked card sections be compressed into each other.
  '[&_.rs-stack-item]:!shrink-0',
  // rsuite buttons are `white-space: nowrap` by default, which clips long
  // labels on narrow screens.
  'max-[425px]:[&_.rs-btn]:!whitespace-normal',
  // Badges stay on one line on larger screens, but stack (wrap) on mobile
  // so they don't overflow the narrow card.
  'max-[425px]:[&_.job-badges-stack]:flex-wrap',
  '[&_p.salary-range]:text-[#212121]',
  '[&_p.salary-range]:leading-[22.4px]',
  '[&_p.salary-range]:font-bold',
  '[&_p.salary-range]:text-[110%]',
  '[&_.rs-stack]:!w-full',
  // Scope the full-width rules to the card's top-level column stack (a
  // direct child of the panel body) only; nested stacks such as the badge
  // row would otherwise get their items stretched to the full card width.
  '[&_.rs-panel-body>.rs-stack>.rs-stack-item:nth-child(2)]:!w-full',
  '[&_.rs-panel-body>.rs-stack>.rs-stack-item:nth-child(3)]:!w-full',
  '[&_.rs-panel-body>.rs-stack>.rs-stack-item:last-child]:!w-full',
  '[&_.rs-col.company-info_p]:line-clamp-1',
  '[&_.rs-col.company-info_b]:text-[1.1em]',
  '[&_.rs-col.company-info_b]:font-bold',
  '[&_.rs-col.company-info_b]:text-black',
  '[&_.rs-col.company-info_b]:line-clamp-2',
  '[&_.rs-col.company-info_b]:min-h-[47.98px]',
  '[&_.rs-col.company-info:last-child_p]:mt-2',
  '[&_.rs-col.company-info:last-child_p]:font-normal',
  '[&_.rs-col.company-info:last-child_p]:leading-[140%]',
  '[&_.rs-col.company-info:last-child_p]:text-[#595959]',
  '[&_.jobs-count>b]:leading-[140%]',
  '[&_.jobs-count>b]:font-semibold',
  '[&_.jobs-count>b]:text-[#212121]',
  '[&_.rs-btn-icon]:!w-full [&_.rs-btn-icon]:!px-2 [&_.rs-btn-icon]:!py-4',
].join(' ');

const SIMILAR_CARD_EXTRA_CLASS = [
  '!min-h-0 !max-h-[375px]',
  '[&_.rs-col.company-info]:!line-clamp-1',
  '[&_.rs-col.company-info_b]:!line-clamp-1',
].join(' ');

const BADGE_CLASS =
  // eslint-disable-next-line max-len
  'rounded-[20px] px-[10px] py-[2px] font-normal whitespace-nowrap text-[#212121]';

type State = {
  id: string | null;
  details: any;
  similarCompanies: Array<any>;

  loading: boolean;
};

class CandidateCompanyDetailsScreen extends React.Component<any, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      id: null,
      details: null,
      loading: true,
      similarCompanies: [],
    };
  }

  componentDidMount(): void {
    const t = _.first(document.getElementsByClassName('rs-content'));
    if (t) {
      _.set(t, 'style.backgroundColor', '#F6F6F6');
    }

    const { params } = this.props;
    const id = _.get(params, 'id');

    this.setState({ id }, () => this.getCompanyDetails());
  }

  componentDidUpdate(prevProps: Readonly<any>): void {
    const prevId = _.get(prevProps, 'params.id');
    const id = _.get(this.props, 'params.id');

    if (prevId !== id) {
      this.setState({ id, loading: true }, () => {
        this.getCompanyDetails();
      });
    }
  }

  private getCompanyDetails = async (): Promise<void> => {
    try {
      const { id } = this.state;
      const res = await CompanyAPI.getCompanyDetails(String(id));
      const data = _.get(res, 'data');

      const industryId = Number(_.get(data, 'industry.id')) || 0;
      const similarCompanies = await CompanyAPI.companyListByIndustry(
        industryId,
        1,
        '',
      );

      this.setState({
        details: data,
        loading: false,
        similarCompanies: _.get(similarCompanies, 'data') || [],
      });
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  private redirectTo = (url: string): void => {
    const { navigate } = this.props;
    navigate(url, { replace: true });
  };

  private onSaveOrRemoveApplication = async (
    jobId: number,
    isSaved: boolean,
  ): Promise<void> => {
    try {
      let message;

      if (isSaved) {
        await JobApplicationAPI.remove(jobId);
        message = 'Job posting has been removed from your bookmark list!';
      } else {
        await JobApplicationAPI.save(jobId);
        message = 'Job posting has been added to your bookmark list!';
      }

      toaster.push(util.buildMessage(message));

      // Re-fetch details
      this.getCompanyDetails();
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  renderSideTabs = ({ location } = this.props): JSX.Element => (
    <Col lg={5} md={5} sm={0} xs={0}>
      <Affix className="tg-affix-side-panel" top={window.innerHeight * 0.15}>
        <Stack spacing={6} direction="column">
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/home')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/home')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaHome />}
          >
            Home
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/search-companies')
            }
            appearance="subtle"
            className={
              String(location.pathname).includes('companies')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaBuilding />}
          >
            Company
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/search-jobs')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/search-jobs')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaSuitcase />}
          >
            Search Job
          </IconButton>
          <IconButton
            onClick={() =>
              this.redirectTo('/dashboard/candidate/my-application')
            }
            appearance="subtle"
            className={
              _.isEqual(
                location.pathname,
                '/dashboard/candidate/my-application',
              )
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaFileAlt />}
          >
            My Application
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/my-cv-maker')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/my-cv-maker')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaMagic />}
          >
            My CV Maker
          </IconButton>
          <IconButton
            onClick={() => this.redirectTo('/dashboard/candidate/profile')}
            appearance="subtle"
            className={
              _.isEqual(location.pathname, '/dashboard/candidate/profile')
                ? 'tg-btn-active'
                : ''
            }
            icon={<FaUser />}
          >
            Profile
          </IconButton>
        </Stack>
      </Affix>
    </Col>
  );

  renderMainContent = (): JSX.Element | null => {
    const { details, loading, similarCompanies } = this.state;
    const { t } = this.props;

    const companyName = _.get(details, 'name');
    const companyId = _.get(this.state, 'id');
    const companyLogo = _.get(details, 'logo.url', '-');
    const companyHeader = _.get(details, 'background.url');
    const companyAddress = _.get(details, 'address');
    const workingTypes = _.get(details, 'workingTypes') || [];
    const benefits = _.get(details, 'benefits') || [];
    const cultures = _.get(details, 'cultures') || [];
    const companyIndustry = _.get(details, 'industry.name', '-');
    const companyDesciption = _.get(details, 'description') || '-';
    const jobPostings = _.get(details, 'openingJobs') || [];
    let companySize = `${_.get(details, 'employeeMinSize')} - ${_.get(
      details,
      'employeeMaxSize',
    )}`;
    if (_.get(details, 'employeeMinSize') > 10000) {
      companySize = '>10000';
    }
    let companyWebsite = _.get(details, 'website') || '';
    companyWebsite = utils.toLink(companyWebsite);

    return (
      <Col
        md={24}
        sm={24}
        xs={24}
        lg={18}
        className={`company-details-main-content ${MAIN_CONTENT_CLASS}`}
      >
        <Row>
          <Col xs={24}>
            <a href="/dashboard/candidate/search-companies">
              <FaChevronLeft />
              <span>Other Company</span>
            </a>
            <Divider style={{ backgroundColor: 'transparent' }} />
          </Col>
          <Col xs={24} sm={17}>
            <Panel
              bodyFill
              className={`company-details-wrapper-card ${WRAPPER_CARD_CLASS}`}
            >
              <div>
                {companyHeader ? (
                  <img src={companyHeader} alt="card-header" width="100%" />
                ) : (
                  util.companyBg(companyIndustry)
                )}
              </div>
              {loading && (
                <Placeholder.Graph
                  // eslint-disable-next-line max-len
                  className="placeholder-logo !absolute !left-[5%] !top-[284px] !h-[84px] !w-[84px] !rounded-full !bg-white"
                  active
                />
              )}
              {!loading && (
                <img
                  // eslint-disable-next-line max-len
                  className="absolute left-[5%] top-[284px] h-[84px] w-[84px] rounded-full bg-white object-contain shadow-[0_5px_10px_rgb(0_0_0_/_10%)]"
                  src={companyLogo}
                  alt="company-logo"
                />
              )}
              <Panel className={`company-details-card ${DETAILS_CARD_CLASS}`}>
                <br />
                {!loading && (
                  <Stack spacing={8}>
                    <h3
                      className="company-details-title"
                      style={{
                        marginTop: '8px',
                      }}
                    >
                      {companyName}
                    </h3>
                    <img
                      className="mt-2 h-6 w-6 object-cover max-[425px]:mt-0"
                      src={`${process.env.PUBLIC_URL}/icon-verified.png`}
                      alt="logo-verified"
                    />
                  </Stack>
                )}
                {loading && (
                  <Stack spacing={8} className="loading-skeleton">
                    <PlaceholderParagraph active />
                  </Stack>
                )}
                <br />
                <Stack spacing={8}>
                  <p className="company-details-title">Location</p>
                </Stack>
                {!loading && (
                  <Stack spacing={8}>
                    <p>{companyAddress}</p>
                  </Stack>
                )}
                {loading && (
                  <Stack spacing={8} className="loading-skeleton">
                    <PlaceholderGrid rows={7} columns={1} active />
                  </Stack>
                )}

                <br />
                <Stack spacing={8}>
                  <p className="company-details-title">Industry</p>
                </Stack>
                {!loading && (
                  <Stack spacing={8}>
                    <p>{companyIndustry}</p>
                  </Stack>
                )}
                {loading && (
                  <Stack spacing={8} className="loading-skeleton">
                    <PlaceholderGrid rows={7} columns={1} active />
                  </Stack>
                )}

                <br />
                <br />
                <Stack spacing={8}>
                  <p className="company-details-title">About the Company</p>
                </Stack>
                <Divider />
                {loading && (
                  <Stack spacing={8} className="loading-skeleton">
                    <PlaceholderGrid rows={7} columns={1} active />
                  </Stack>
                )}
                {!loading && (
                  <Stack spacing={8}>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: companyDesciption,
                      }}
                    />
                  </Stack>
                )}

                <br />
                <br />
                <Stack spacing={8}>
                  <p className="company-details-title">
                    The Culture of Our Company
                  </p>
                </Stack>
                <Divider />
                {loading && (
                  <Stack spacing={8} className="loading-skeleton">
                    <PlaceholderGrid rows={7} columns={1} active />
                  </Stack>
                )}
                <Row>
                  {_.map(cultures, (c: any, index: number) => (
                    <Col
                      key={c.name}
                      xs={24}
                      sm={12}
                      md={12}
                      lg={8}
                      className={cultureColClass(index)}
                    >
                      <img
                        src={
                          _.get(c, 'logo.url') ||
                          'https://via.placeholder.com/150'
                        }
                        alt="company-culture-logo"
                      />
                      <b>{c.name}</b>
                      <p className="culture-text">
                        {_.get(c, 'description') || '-'}
                      </p>
                    </Col>
                  ))}
                </Row>
              </Panel>
            </Panel>
          </Col>

          <Col xs={24} sm={7}>
            <Panel className={`company-details-side-card ${SIDE_CARD_CLASS}`}>
              <Stack
                spacing={8}
                direction="column"
                style={{ alignItems: 'flex-start' }}
              >
                <Row>
                  <Col xs={3}>
                    <FaUsers />
                  </Col>
                  <Col xs={21}>
                    <p className="company-details-attribute-title">
                      Company Size
                    </p>
                    {loading && <PlaceholderGrid rows={1} columns={1} active />}
                    {!loading && <p>{companySize}</p>}
                  </Col>
                </Row>
                <Row>
                  <Col xs={3}>
                    <FaBuilding />
                  </Col>
                  <Col xs={21}>
                    <p className="company-details-attribute-title">
                      Working Type
                    </p>
                    {loading && <PlaceholderGrid rows={1} columns={1} active />}
                    {!loading && (
                      <p>
                        {workingTypes.length > 1 &&
                          _.reduce(
                            workingTypes,
                            (w: string, val: any) => {
                              if (_.isEmpty(w)) {
                                return `${val.name} `;
                              }

                              return `${w}, ${val.name}`;
                            },
                            '',
                          )}

                        {workingTypes.length === 1 &&
                          _.get(workingTypes, '0.name')}
                        {workingTypes.length === 0 && '-'}
                      </p>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col xs={3}>
                    <FaStar />
                  </Col>
                  <Col xs={21}>
                    <p className="company-details-attribute-title">Benefit</p>
                    {loading && <PlaceholderGrid rows={1} columns={1} active />}
                    {!loading && (
                      <p>
                        {benefits.length > 1 &&
                          _.reduce(
                            benefits,
                            (b: string, val: any) => {
                              if (_.isEmpty(b)) {
                                return `${val.name} `;
                              }

                              return `${b}, ${val.name}`;
                            },
                            '',
                          )}

                        {benefits.length === 1 && _.get(benefits, '0.name')}
                        {benefits.length === 0 && '-'}
                      </p>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col xs={3}>
                    <FaLink />
                  </Col>
                  <Col xs={21}>
                    <p className="company-details-attribute-title">Website</p>
                    {loading && <PlaceholderGrid rows={1} columns={1} active />}
                    {!loading && (
                      <a href={companyWebsite} target="_blank" rel="noreferrer">
                        {companyWebsite}
                      </a>
                    )}
                  </Col>
                </Row>
              </Stack>
            </Panel>
          </Col>

          <Col xs={24}>
            <Panel
              // eslint-disable-next-line max-len
              className={`company-details-card company-job-opening-card ${DETAILS_CARD_CLASS} ${SECTION_CARD_EXTRA_CLASS}`}
            >
              <br />
              {!loading && (
                <Stack spacing={8}>
                  <h3
                    className="company-details-title"
                    style={{
                      marginTop: '8px',
                    }}
                  >
                    Our Job Openings Right Now!
                  </h3>
                </Stack>
              )}
              {loading && (
                <Stack spacing={8} className="loading-skeleton">
                  <PlaceholderParagraph active />
                </Stack>
              )}

              <br />
              <Row className="search-results-row">
                {_.map(jobPostings, (obj: any, idx: number) => {
                  const employmentStatus = _.get(
                    obj,
                    'employmentStatus.0.name',
                  );
                  const wTypes = _.get(obj, 'workingTypes.0.name');
                  const salaryHigh: number = _.get(obj, 'maxSalaryRange') || 0;
                  const salaryLow: number = _.get(obj, 'minSalaryRange') || 0;
                  const duration = _.get(obj, 'duration');
                  const jobId = _.get(obj, 'id');
                  const isSaved = _.get(obj, 'isSaved');
                  const isHot = _.get(obj, 'hot');

                  let employmentBadgeClass = '!bg-[#FFDE59]';
                  if (employmentStatus === 'Full Time') {
                    employmentBadgeClass = '!bg-[#003884] !text-white';
                  } else if (!wTypes) {
                    employmentBadgeClass = '!bg-[#90BFFF]';
                  }

                  return (
                    <Col
                      key={`col-searchresults-${idx}`}
                      xs={24}
                      sm={12}
                      md={12}
                      lg={8}
                    >
                      <Panel
                        id="job-openings"
                        // eslint-disable-next-line max-len
                        className={`p-4 py-2 company-details-search-result-card !cursor-default ${SEARCH_RESULT_CARD_CLASS}`}
                      >
                        <Stack
                          direction="column"
                          spacing={20}
                          style={{ alignItems: 'flex-start' }}
                        >
                          {/* Badge */}
                          <Stack spacing={8} className="job-badges-stack">
                            {Boolean(isHot) && (
                              <Badge
                                className={`${BADGE_CLASS} !bg-[#E9A0FF]`}
                                content="New"
                              />
                            )}
                            <Badge
                              // eslint-disable-next-line max-len
                              className={`${BADGE_CLASS} ${employmentBadgeClass}`}
                              content={employmentStatus}
                            />
                            {wTypes && (
                              <Badge
                                key={`workingTypes-${wTypes}`}
                                className={`${BADGE_CLASS} !bg-[#90BFFF]`}
                                content={wTypes}
                              />
                            )}
                          </Stack>

                          {/* Company Information */}
                          <Stack spacing={6} className="w-[fit-content]">
                            <Stack.Item className="w-[fit-content]">
                              <Row>
                                <Col xs={6}>
                                  <Avatar
                                    circle
                                    size="lg"
                                    src={companyLogo}
                                    alt="logo-startup"
                                  />
                                </Col>
                                <Col xs={18} className="company-info">
                                  <Whisper
                                    placement="topStart"
                                    speaker={
                                      <Tooltip>
                                        <b>{companyName}</b>
                                      </Tooltip>
                                    }
                                  >
                                    <p>{companyName}</p>
                                  </Whisper>
                                  <Whisper
                                    placement="topStart"
                                    speaker={
                                      <Tooltip>
                                        <b>{_.get(obj, 'title')}</b>
                                      </Tooltip>
                                    }
                                  >
                                    <b>{_.get(obj, 'title')}</b>
                                  </Whisper>
                                </Col>
                              </Row>
                            </Stack.Item>
                          </Stack>

                          {/* Job Details */}
                          <Stack direction="column" spacing={8}>
                            <Stack.Item className="w-100">
                              <Row>
                                <Col xs={3}>
                                  <FaMapMarkerAlt />
                                </Col>
                                <Col xs={21}>
                                  <p>{_.get(details, 'region')}</p>
                                </Col>
                              </Row>
                            </Stack.Item>
                            <Stack.Item className="w-100">
                              <Row>
                                <Col xs={3}>
                                  <FaClock />
                                </Col>
                                <Col xs={21}>
                                  {duration > 0 && <p>{duration} month(s)</p>}
                                  {duration === 0 && <p>Full Time</p>}
                                </Col>
                              </Row>
                            </Stack.Item>
                            <Stack.Item className="w-100">
                              <Row>
                                <Col xs={3}>
                                  <FaBuilding />
                                </Col>
                                <Col xs={21}>
                                  <p>{companyIndustry}</p>
                                </Col>
                              </Row>
                            </Stack.Item>
                          </Stack>

                          {/* Salary range */}
                          <Stack spacing={6}>
                            <p className="salary-range">
                              {util.formatSalaryRange(salaryLow, salaryHigh)}
                            </p>
                          </Stack>

                          {/* Job Card Buttons */}
                          <Stack spacing={6}>
                            <Stack.Item className="w-full">
                              <Row>
                                <Col xs={19}>
                                  <Button
                                    className="btn-tg-primary"
                                    block
                                    appearance="primary"
                                    disabled={!obj.active}
                                    onClick={() => {
                                      // eslint-disable-next-line max-len
                                      const url = `/dashboard/candidate/jobs/${jobId}`;
                                      window.open(url);
                                    }}
                                  >
                                    {obj.active
                                      ? t(
                                          constant.translation.searchJob
                                            .formApplyButtonKey,
                                        )
                                      : 'Closed'}
                                  </Button>
                                </Col>
                                <Col xs={5}>
                                  <IconButton
                                    onClick={() =>
                                      this.onSaveOrRemoveApplication(
                                        jobId,
                                        isSaved,
                                      )
                                    }
                                    appearance="default"
                                    size="lg"
                                    disabled={!_.get(obj, 'active')}
                                    icon={
                                      isSaved ? (
                                        <img
                                          // eslint-disable-next-line max-len
                                          src={`${process.env.PUBLIC_URL}/bookmark-logo-filled.png`}
                                          alt="bookmark"
                                          width={18}
                                          height={24}
                                        />
                                      ) : (
                                        <img
                                          // eslint-disable-next-line max-len
                                          src={`${process.env.PUBLIC_URL}/bookmark-logo.png`}
                                          alt="bookmark"
                                          width={18}
                                          height={24}
                                        />
                                      )
                                    }
                                  />
                                </Col>
                              </Row>
                            </Stack.Item>
                          </Stack>
                        </Stack>
                      </Panel>
                    </Col>
                  );
                })}

                {!loading && jobPostings.length === 0 && (
                  <p className="no-results-text pt-6 pb-8">No results</p>
                )}
              </Row>
            </Panel>
          </Col>

          <Col
            xs={24}
            style={{
              display:
                similarCompanies.length <= 1 &&
                _.isEqual(_.get(similarCompanies, '0.id'), _.get(details, 'id'))
                  ? 'none'
                  : '',
            }}
          >
            <Panel
              // eslint-disable-next-line max-len
              className={`company-details-card company-job-opening-card ${DETAILS_CARD_CLASS} ${SECTION_CARD_EXTRA_CLASS}`}
            >
              <br />
              {!loading && (
                <Stack spacing={8}>
                  <h3
                    className="company-details-title"
                    style={{
                      marginTop: '8px',
                    }}
                  >
                    Similar Company
                  </h3>
                </Stack>
              )}
              {loading && (
                <Stack spacing={8} className="loading-skeleton">
                  <PlaceholderParagraph active />
                </Stack>
              )}

              <br />
              {similarCompanies.length > 0 && (
                <Row>
                  {_.chain(similarCompanies)
                    .map((obj: any) => {
                      const cName = _.get(obj, 'name');
                      const cHeader = _.get(obj, 'background.url');
                      const industryName = _.get(obj, 'industryName');
                      const openingJobCount = _.get(obj, 'openingJobCount');
                      const cLogo = _.get(obj, 'logo.url');
                      const isShow = String(obj.id) !== companyId;

                      return (
                        isShow && (
                          <Col key={obj.id} xs={24} sm={12} md={12} lg={8}>
                            <Panel
                              bodyFill
                              // eslint-disable-next-line max-len
                              className={`company-details-search-result-card similar-company-card ${SEARCH_RESULT_CARD_CLASS} ${SIMILAR_CARD_EXTRA_CLASS}`}
                              onClick={() => {
                                const { navigate } = this.props;

                                navigate(
                                  `/dashboard/candidate/companies/${_.get(
                                    obj,
                                    'id',
                                  )}`,
                                );
                              }}
                            >
                              {cHeader ? (
                                <img
                                  src={cHeader}
                                  alt="card-header"
                                  width="100%"
                                />
                              ) : (
                                util.companyBg(industryName)
                              )}
                              <img src={cLogo} alt="company-logo" />
                              <Panel>
                                <Stack
                                  direction="column"
                                  spacing={14}
                                  style={{ alignItems: 'flex-start' }}
                                >
                                  {/* Company Information */}
                                  <Stack spacing={6}>
                                    <Row>
                                      <Col xs={24} className="company-info">
                                        <Whisper
                                          placement="topStart"
                                          speaker={
                                            <Tooltip>
                                              <b>{cName}</b>
                                            </Tooltip>
                                          }
                                        >
                                          <b>{cName}</b>
                                        </Whisper>
                                      </Col>
                                      <Col xs={24} className="company-info">
                                        <p>{industryName}</p>
                                      </Col>
                                    </Row>
                                  </Stack>

                                  {/* Job Counts */}
                                  <Stack
                                    direction="column"
                                    spacing={6}
                                    alignItems="flex-start"
                                  >
                                    <Row>
                                      <Col xs={3}>
                                        <FaSuitcase />
                                      </Col>
                                      <Col xs={21} className="jobs-count">
                                        <b>{openingJobCount} Jobs Opening</b>
                                      </Col>
                                    </Row>
                                  </Stack>
                                </Stack>
                              </Panel>
                            </Panel>
                          </Col>
                        )
                      );
                    })
                    .valueOf()}
                </Row>
              )}
            </Panel>
          </Col>
        </Row>
      </Col>
    );
  };

  render(): JSX.Element | null {
    return (
      <Grid className="home-static-authorized-container" fluid>
        <Row>
          {this.renderSideTabs()}
          {this.renderMainContent()}
        </Row>
      </Grid>
    );
  }
}

export default withRouter(withTranslation()(CandidateCompanyDetailsScreen));
